import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

import { useSeo, ORG_JSONLD } from "@/lib/seo";
import { SEARCH_DESCRIPTION } from "@/data/search";
import { SITE } from "@/data/site";
import IMAGES from "@/data/images";
import { THEMES } from "@/data/themes";
import { ARTICLES } from "@/data/articles";
import { LOCATIONS, getLocation } from "@/data/locations";

import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";
import { SectionLabel } from "@/components/site/SectionLabel";
import { SSButton } from "@/components/site/SSButton";
import { GeographicIntelligence } from "@/components/site/GeographicIntelligence";
import { ArticleCard } from "@/components/site/ArticleCard";
import { ContextualCTA } from "@/components/site/ContextualCTA";
import { ContourField } from "@/components/site/ContourField";

const Hero = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.42, 0.72]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[600px] overflow-hidden" data-testid="hero">
      <motion.div className="absolute inset-0" style={reduce ? {} : { scale: imgScale, y: imgY }}>
        <img src={IMAGES.heroRidge.url} alt={IMAGES.heroRidge.alt} className="h-full w-full object-cover" />
      </motion.div>
      <motion.div className="absolute inset-0 bg-[#10110f]" style={{ opacity: reduce ? 0.5 : overlay }} />
      <div className="absolute inset-0 bg-gradient-to-t from-[#10110f]/70 via-transparent to-[#10110f]/30" />

      <motion.div className="relative h-full ss-container flex flex-col justify-end pb-20 lg:pb-28" style={reduce ? {} : { y: textY }}>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
          className="ss-overline text-[#F7F5F0]/70 mb-6"
        >
          Property Intelligence & Advisory · Uttarakhand
        </motion.p>

        <h1 className="font-display text-[#F7F5F0] font-light leading-[0.92] tracking-tight">
          <span className="block overflow-hidden">
            <motion.span
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.45 }}
              className="block text-[2.6rem] sm:text-7xl lg:text-8xl xl:text-[8.5rem]"
            >
              Property Intelligence
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.62 }}
              className="block text-[2.6rem] sm:text-7xl lg:text-8xl xl:text-[8.5rem] text-[#F7F5F0]/55"
            >
              for Uttarakhand
            </motion.span>
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-7 max-w-3xl"
        >
          <p className="text-[#F7F5F0]/80 text-base sm:text-lg font-light leading-relaxed max-w-md">
            Independent research into locations, land, law and due diligence — so you understand a place before you buy into it.
          </p>
          <div className="flex flex-wrap gap-4">
            <SSButton to="/intelligence" variant="light" data-testid="hero-explore">Explore Intelligence</SSButton>
            <SSButton to="/advisory" variant="ghost" className="text-[#F7F5F0] border-[#F7F5F0]/40 hover:border-[#F7F5F0]" arrow={false} data-testid="hero-discuss">
              Discuss a Decision
            </SSButton>
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-7 right-6 lg:right-16 hidden sm:flex items-center gap-2 text-[#F7F5F0]/60"
      >
        <span className="font-mono-ss text-[0.64rem] tracking-widest uppercase">Scroll</span>
        <ArrowDown className="h-4 w-4 animate-bounce" strokeWidth={1.5} />
      </motion.div>
    </section>
  );
};

const Intro = () => (
  <section className="ss-container py-24 lg:py-36" data-testid="home-intro">
    <div className="grid lg:grid-cols-[0.8fr_1.2fr] gap-10 lg:gap-20">
      <div>
        <SectionLabel marker="01">The Practice</SectionLabel>
      </div>
      <div>
        <p className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-[1.18] text-[#1A1C1E]">
          Selling Sunsets is an independent property-intelligence and buyer-side advisory practice based in Dehradun. We are not a listings portal. We help people understand Uttarakhand — its locations, land, regulation and risks — before a major property decision is made.
        </p>
        <div className="mt-10 grid sm:grid-cols-3 gap-8">
          {[
            { k: "Independent", v: "Buyer-side research, not seller-driven marketing." },
            { k: "Evidence-led", v: "Claims traced to primary sources, with nothing invented." },
            { k: "Locally grounded", v: "Across Garhwal and Kumaon, not a single town." },
          ].map((it) => (
            <div key={it.k}>
              <p className="ss-overline text-[#8C6A53] mb-2">{it.k}</p>
              <p className="text-[0.95rem] leading-relaxed text-[#52575D] font-light">{it.v}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Geography = () => (
  <section className="relative bg-[#EFECE6] py-24 lg:py-32" data-testid="home-geography">
    <div className="ss-container">
      <div className="max-w-2xl mb-16">
        <SectionLabel marker="02" className="mb-6">Geographic Intelligence</SectionLabel>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.02] text-[#1A1C1E]">
          One state. <br />Many very different markets.
        </h2>
        <p className="mt-6 text-lg font-light text-[#52575D] max-w-xl">
          From the Doon Valley floor to the ridgelines of Kumaon, each location carries its own terrain, access, regulation and risk. Select a place to begin.
        </p>
      </div>
      <GeographicIntelligence />
    </div>
  </section>
);

const Themes = () => (
  <section className="ss-container py-24 lg:py-32" data-testid="home-themes">
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
      <div className="max-w-xl">
        <SectionLabel marker="03" className="mb-6">Intelligence Themes</SectionLabel>
        <h2 className="font-display text-4xl sm:text-5xl font-light leading-tight text-[#1A1C1E]">
          Seven ways we read a property decision
        </h2>
      </div>
      <Link to="/intelligence" className="group inline-flex items-center gap-2 text-[0.78rem] uppercase tracking-[0.1em] text-[#1A1C1E] shrink-0" data-testid="themes-view-all">
        <span className="ss-link">All intelligence</span>
        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
      </Link>
    </div>

    <RevealGroup className="border-t border-[#E2DDD5]">
      {THEMES.map((t) => (
        <RevealItem key={t.slug}>
          <Link
            to={`/intelligence/${t.slug}`}
            className="group grid md:grid-cols-[auto_1fr_auto] gap-4 md:gap-8 items-baseline py-7 border-b border-[#E2DDD5] transition-colors hover:bg-[#F2EFE9]/60"
            data-testid={`theme-row-${t.slug}`}
          >
            <span className="font-mono-ss text-[0.7rem] tracking-widest text-[#C2A374]">{t.index}</span>
            <div>
              <h3 className="font-display text-2xl sm:text-3xl font-normal text-[#1A1C1E] group-hover:text-[#2D3B32] transition-colors">{t.title}</h3>
              <p className="mt-2 text-[0.95rem] font-light text-[#52575D] max-w-2xl">{t.summary}</p>
            </div>
            <ArrowUpRight className="hidden md:block h-5 w-5 text-[#8A9098] transition-transform group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-[#1A1C1E]" strokeWidth={1.3} />
          </Link>
        </RevealItem>
      ))}
    </RevealGroup>
  </section>
);

const LocationFeature = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const dehradun = getLocation("dehradun");

  return (
    <section ref={ref} className="relative overflow-hidden bg-[#181A1B]" data-testid="home-location-feature">
      <div className="grid lg:grid-cols-2">
        <div className="relative h-[55vh] lg:h-auto min-h-[420px] overflow-hidden">
          <motion.img
            src={IMAGES.dehradun.url}
            alt={IMAGES.dehradun.alt}
            style={reduce ? {} : { y }}
            className="absolute inset-0 h-[116%] w-full object-cover"
          />
        </div>
        <div className="relative flex items-center px-7 py-20 sm:px-14 lg:px-20">
          <ContourField className="absolute right-0 top-0 h-full w-2/3 opacity-[0.08]" stroke="#F7F5F0" opacity={0.6} />
          <div className="relative">
            <p className="font-mono-ss text-[0.7rem] tracking-widest text-[#C2A374] mb-5">{dehradun.coords} · {dehradun.elevation}</p>
            <SectionLabel marker="04" tone="light" className="mb-6">Editorial Location</SectionLabel>
            <h2 className="font-display text-6xl sm:text-7xl lg:text-8xl font-light text-[#F7F5F0] leading-[0.9]">Dehradun</h2>
            <p className="mt-6 text-lg font-light text-[#F7F5F0]/75 max-w-md leading-relaxed">
              The valley capital — deepest infrastructure, widest spread of property types, and the most complex planning landscape in the state.
            </p>
            <div className="mt-9">
              <SSButton to="/locations/dehradun" variant="light" data-testid="feature-dehradun">Read Dehradun intelligence</SSButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const HowWeHelp = () => (
  <section className="ss-container py-24 lg:py-32" data-testid="home-how-we-help">
    <div className="grid lg:grid-cols-[1fr_1fr] gap-12 lg:gap-20 items-center">
      <div>
        <SectionLabel marker="05" className="mb-6">How We Help</SectionLabel>
        <h2 className="font-display text-4xl sm:text-5xl font-light leading-tight text-[#1A1C1E]">
          Independent research, on the buyer's side
        </h2>
        <p className="mt-6 text-lg font-light text-[#52575D] leading-relaxed">
          We work for the person buying, not the person selling. Our role is to help you understand a location, test a property's claims, and structure the questions worth investigating — before, not after, you commit.
        </p>
        <div className="mt-10 space-y-6">
          {[
            ["Location intelligence", "Clear-eyed reading of terrain, access, regulation and character, place by place."],
            ["Due-diligence structuring", "The checks and questions a property should survive before purchase."],
            ["Buyer-side advisory", "A direct conversation with a real advisor who knows the ground."],
          ].map(([t, d]) => (
            <div key={t} className="flex gap-5 border-t border-[#E2DDD5] pt-5">
              <span className="font-display text-2xl text-[#C2A374]">—</span>
              <div>
                <h3 className="font-display text-xl text-[#1A1C1E]">{t}</h3>
                <p className="mt-1 text-[0.95rem] font-light text-[#52575D]">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Reveal className="relative">
        <div className="ss-hover-grow overflow-hidden aspect-[4/5]">
          <img src={IMAGES.villaStone.url} alt={IMAGES.villaStone.alt} className="h-full w-full object-cover" />
        </div>
        <p className="mt-3 font-mono-ss text-[0.64rem] tracking-wider text-[#8A9098]">Editorial illustration · hillside architecture</p>
      </Reveal>
    </div>
  </section>
);

const GuidedTeaser = () => (
  <section className="bg-[#2D3B32] text-[#F7F5F0] py-24 lg:py-32" data-testid="home-guided-teaser">
    <div className="ss-container grid lg:grid-cols-[1fr_auto] gap-10 lg:items-center">
      <div className="max-w-2xl">
        <SectionLabel marker="06" tone="light" className="mb-6">Guided Intelligence</SectionLabel>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.02] text-[#F7F5F0]">
          Not sure where to begin?
        </h2>
        <p className="mt-6 text-lg font-light text-[#F7F5F0]/75 leading-relaxed">
          Answer a few questions and we'll assemble a research starting point — the location guides, regulatory reading and due-diligence checklist most relevant to what you're considering. No property is "analysed"; this simply points you to the right intelligence.
        </p>
      </div>
      <SSButton to="/guided" variant="light" data-testid="guided-teaser-cta">Begin guided intelligence</SSButton>
    </div>
  </section>
);

const LatestJournal = () => {
  const latest = ARTICLES.slice(0, 3);
  return (
    <section className="ss-container py-24 lg:py-32" data-testid="home-journal">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div className="max-w-xl">
          <SectionLabel marker="07" className="mb-6">Intelligence Library</SectionLabel>
          <h2 className="font-display text-4xl sm:text-5xl font-light leading-tight text-[#1A1C1E]">Latest research</h2>
        </div>
        <Link to="/journal" className="group inline-flex items-center gap-2 text-[0.78rem] uppercase tracking-[0.1em] text-[#1A1C1E] shrink-0" data-testid="journal-view-all">
          <span className="ss-link">All of the Journal</span>
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
        </Link>
      </div>
      <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
        {latest.map((a, i) => (
          <ArticleCard key={a.slug} article={a} index={i} />
        ))}
      </div>
    </section>
  );
};

export default function Home() {
  useSeo({
    title: null,
    description:
      SEARCH_DESCRIPTION,
    path: "/",
    image: IMAGES.heroRidge.url,
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        ORG_JSONLD,
        { "@type": "WebSite", name: "Selling Sunsets", url: "https://sellingsunsets.co.in" },
      ],
    },
  });

  return (
    <>
      <Hero />
      <Intro />
      <Geography />
      <Themes />
      <LocationFeature />
      <HowWeHelp />
      <GuidedTeaser />
      <LatestJournal />
      <ContextualCTA
        eyebrow="Enquiry"
        title="Considering property in Uttarakhand?"
        text="Request an independent buyer-side assessment. Tell us what you're weighing and we'll help you structure the decision."
        primaryLabel="Request an assessment"
        primaryTo="/advisory"
        whatsappMessage="Hi Suhail, I'm considering property in Uttarakhand and would like an independent assessment."
      />
    </>
  );
}

